<script lang="ts" context="module">
  export const matchingProps = [];
</script>

<script lang="ts">
  // *** This file is part of DbGate Premium ***

  import _ from 'lodash';
  import ToolStripContainer from '../buttons/ToolStripContainer.svelte';
  import ToolStripButton from '../buttons/ToolStripButton.svelte';
  import openNewTab from '../utility/openNewTab';
  import { getCurrentConfig } from '../stores';
  import { apiCall } from '../utility/api';
  import { format, startOfDay, endOfDay } from 'date-fns';
  import { getBoolSettingsValue, getIntSettingsValue } from '../settings/settingsTools';
  import { getConnectionLabel, safeJsonParse } from 'dbgate-tools';
  import DateRangeSelector from '../elements/DateRangeSelector.svelte';
  import TabControl from '../elements/TabControl.svelte';
  import AceEditor from '../query/AceEditor.svelte';
  import DropDownButton from '../buttons/DropDownButton.svelte';
  import { showModal } from '../modals/modalTools';
  import ValueLookupModal from '../modals/ValueLookupModal.svelte';
  import Chip from '../elements/Chip.svelte';
  import { useSettings } from '../utility/metadataLoaders';
  import ErrorInfo from '../elements/ErrorInfo.svelte';
  import Link from '../elements/Link.svelte';

  let loadedRows = [];
  let loadedAll = false;
  let domLoadNext;
  let observer;
  let dateFilter = [new Date(), new Date()];
  let selectedLogId = null;
  let selectedLogDetail = null;
  let filters = {};

  const settings = useSettings();

  function formatPossibleUuid(value) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (_.isString(value) && value.match(uuidRegex)) {
      return value.slice(0, 8);
    }
    if (value == null) {
      return 'N/A';
    }
    return value;
  }

  async function loadNextRows() {
    const pageSize = getIntSettingsValue('dataGrid.pageSize', 100, 5, 1000);
    const rows = await apiCall('storage/get-audit-log', {
      offset: loadedRows.length,
      limit: pageSize,
      dateFrom: startOfDay(dateFilter[0]).getTime(),
      dateTo: endOfDay(dateFilter[1]).getTime(),
      filters,
    });
    loadedRows = [...loadedRows, ...rows];
    if (rows.length < 10) {
      loadedAll = true;
    }
  }

  function startObserver(dom) {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (dom) {
      observer = new IntersectionObserver(entries => {
        if (entries.find(x => x.isIntersecting)) {
          loadNextRows();
        }
      });
      observer.observe(dom);
    }
  }

  $: startObserver(domLoadNext);

  function getConnectionString({ conid, connectionData }) {
    if (conid === '__storage') {
      return 'Internal Storage';
    }
    if (!_.isEmpty(connectionData)) {
      return getConnectionLabel(connectionData);
    }
    return '';
  }

  function reloadData() {
    loadedRows = [];
    loadedAll = false;
  }

  function doSetFilter(field, values) {
    filters = {
      ...filters,
      [field]: values,
    };
    reloadData();
  }

  function filterBy(field) {
    showModal(ValueLookupModal, {
      conid: '__storage',
      database: getCurrentConfig()?.storageDatabase,
      pureName: 'audit_log',
      field,
      multiselect: true,
      onConfirm: values => {
        doSetFilter(field, values);
      },
    });
  }

  $: if (selectedLogId) {
    apiCall('storage/get-audit-log-detail', { id: selectedLogId }).then(data => {
      selectedLogDetail = data;
    });
  } else {
    selectedLogDetail = null;
  }

  const ColumnNamesMap = {
    user_login: 'login',
    pure_name: 'table',
    conid: 'connection',
  };

  function tryFormatJson(value) {
    const parsed = safeJsonParse(value);
    if (parsed) {
      return JSON.stringify(parsed, null, 2);
    }
    return value;
  }
</script>

{#if $settings?.['storage.useAuditLog'] == 'true'}
  <ToolStripContainer>
    <div class="wrapper">
      <div class="filters">
        <div class="filter-label">Date:</div>
        <DateRangeSelector
          onChange={value => {
            dateFilter = value;
            reloadData();
          }}
        />
        <div class="ml-2">
          <DropDownButton
            data-testid="AdminAuditLogTab_addFilter"
            icon="icon filter"
            menu={[
              { text: 'Login', onClick: () => filterBy('user_login') },
              { text: 'Database', onClick: () => filterBy('database') },
              { text: 'Category', onClick: () => filterBy('category') },
              { text: 'Component', onClick: () => filterBy('component') },
              { text: 'Action', onClick: () => filterBy('action') },
              { text: 'Severity', onClick: () => filterBy('severity') },
              { text: 'Event', onClick: () => filterBy('event') },
              { text: 'Table name', onClick: () => filterBy('pure_name') },
            ]}
          />
        </div>
        {#each Object.keys(filters) as filterKey}
          <div class="ml-2">
            <span class="filter-label">{ColumnNamesMap[filterKey] || filterKey}:</span>
            {#each filters[filterKey] as value}
              <Chip
                onClose={() => {
                  filters = { ...filters, [filterKey]: filters[filterKey].filter(x => x !== value) };
                  if (!filters[filterKey].length) {
                    filters = _.omit(filters, filterKey);
                  }
                  reloadData();
                }}
              >
                {formatPossibleUuid(value)}
              </Chip>
            {/each}
          </div>
        {/each}
      </div>
      <div class="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Message</th>
              <th>Login</th>
              <th>Connection</th>
              <th>Database</th>
            </tr>
          </thead>
          <tbody>
            {#each loadedRows as row}
              <tr
                class="clickable"
                on:click={() => {
                  if (selectedLogId === row.id) {
                    selectedLogId = null;
                  } else {
                    selectedLogId = row.id;
                  }
                }}
              >
                <td>{format(new Date(parseInt(row.created)), 'yyyy-MM-dd')}</td>
                <td>{format(new Date(parseInt(row.created)), 'HH:mm:ss')}</td>
                <td>{row.message || row.event}</td>
                <td>{row.userLogin || ''}</td>
                <td>{getConnectionString(row)}</td>
                <td>{row.database || ''}</td>
              </tr>
              {#if row.id === selectedLogId && selectedLogDetail?.id === row.id}
                <tr>
                  <td colspan="6">
                    <TabControl
                      isInline
                      tabs={_.compact([
                        { label: 'Details', slot: 1 },
                        { label: 'JSON', slot: 2 },
                        _.isString(selectedLogDetail.detail) ? { label: 'Text', slot: 3 } : null,
                      ])}
                    >
                      <svelte:fragment slot="1">
                        <div class="details-wrap">
                          <div class="row">
                            <div>Message:</div>
                            {selectedLogDetail.message}
                          </div>
                          <div class="row">
                            <div>Created:</div>
                            <b>{format(new Date(parseInt(selectedLogDetail.created)), 'yyyy-MM-dd HH:mm:ss')}</b>
                          </div>
                          <div class="row">
                            <div>Modified:</div>
                            <b
                              >{selectedLogDetail.modified
                                ? format(new Date(parseInt(selectedLogDetail.modified)), 'yyyy-MM-dd HH:mm:ss')
                                : 'N/A'}</b
                            >
                          </div>
                          <div class="row">
                            <div>Category:</div>
                            <Link onClick={() => doSetFilter('category', [selectedLogDetail.category])}
                              >{selectedLogDetail.category || 'N/A'}</Link
                            >
                          </div>
                          <div class="row">
                            <div>Component:</div>
                            <Link onClick={() => doSetFilter('component', [selectedLogDetail.component])}
                              >{selectedLogDetail.component || 'N/A'}</Link
                            >
                          </div>
                          <div class="row">
                            <div>Action:</div>
                            <Link onClick={() => doSetFilter('action', [selectedLogDetail.action])}
                              >{selectedLogDetail.action || 'N/A'}</Link
                            >
                          </div>
                          <div class="row">
                            <div>Severity:</div>
                            <Link onClick={() => doSetFilter('severity', [selectedLogDetail.severity])}
                              >{selectedLogDetail.severity || 'N/A'}</Link
                            >
                          </div>
                          <div class="row">
                            <div>Event:</div>
                            <Link onClick={() => doSetFilter('event', [selectedLogDetail.event])}
                              >{selectedLogDetail.event || 'N/A'}</Link
                            >
                          </div>
                          <div class="row">
                            <div>Session:</div>
                            <Link onClick={() => doSetFilter('session_id', [selectedLogDetail.sessionId])}
                              >{formatPossibleUuid(selectedLogDetail.sessionId) || 'N/A'}</Link
                            >
                          </div>
                          <div class="row">
                            <div>Connection:</div>
                            <Link onClick={() => doSetFilter('conid', [selectedLogDetail.conid])}
                              >{formatPossibleUuid(selectedLogDetail.conid) || 'N/A'}</Link
                            >
                          </div>
                          {#if selectedLogDetail.databaase}
                            <div class="row">
                              <div>Database:</div>
                              <Link onClick={() => doSetFilter('database', [selectedLogDetail.database])}
                                >{selectedLogDetail.database || 'N/A'}</Link
                              >
                            </div>
                          {/if}
                          {#if selectedLogDetail.pureName}
                            <div class="row">
                              <div>Table name:</div>
                              <Link onClick={() => doSetFilter('pure_name', [selectedLogDetail.pureName])}
                                >{selectedLogDetail.pureName || 'N/A'}</Link
                              >
                            </div>
                          {/if}
                          {#if selectedLogDetail.sumint1}
                            <div class="row">
                              <div>Row count:</div>
                              {selectedLogDetail.sumint1}
                            </div>
                          {/if}
                        </div></svelte:fragment
                      >
                      <svelte:fragment slot="2">
                        <pre>{JSON.stringify(selectedLogDetail, null, 2)}</pre>
                      </svelte:fragment>
                      <svelte:fragment slot="3">
                        <div class="editor-wrap">
                          {#if _.isString(selectedLogDetail.detail)}
                            <AceEditor
                              value={tryFormatJson(selectedLogDetail.detail)}
                              readOnly
                              mode={safeJsonParse(selectedLogDetail.detail) ? 'json' : 'sql'}
                            />
                          {/if}
                        </div>
                      </svelte:fragment>
                    </TabControl>
                  </td>
                </tr>
              {/if}
            {/each}
            {#if !loadedRows?.length}
              <tr>
                <td colspan="6">No data for selected date</td>
              </tr>
            {/if}
            {#if !loadedAll}
              {#key loadedRows}
                <tr>
                  <td colspan="6" bind:this={domLoadNext}>Loading next rows... </td>
                </tr>
              {/key}
            {/if}
          </tbody>
        </table>
      </div>
    </div>

    <svelte:fragment slot="toolstrip">
      <ToolStripButton
        icon="icon refresh"
        data-testid="AdminAuditLogTab_refreshButton"
        on:click={() => {
          reloadData();
        }}>Refresh</ToolStripButton
      >
      <ToolStripButton
        icon="icon table"
        on:click={() => {
          openNewTab({
            title: 'audit_log',
            icon: 'img table',
            tabComponent: 'TableDataTab',
            props: {
              pureName: 'audit_log',
              conid: '__storage',
              database: getCurrentConfig()?.storageDatabase,
              objectTypeField: 'tables',
            },
          });
        }}>Open table</ToolStripButton
      >
    </svelte:fragment>
  </ToolStripContainer>
{:else}
  <ErrorInfo message="Audit log is not enabled, you could enable it in Administration &gt; Settings" alignTop />
{/if}

<style>
  .editor-wrap {
    height: 200px;
  }
  .tablewrap {
    overflow: auto;
    flex: 1;
  }
  .wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  table.disableFocusOutline:focus {
    outline: none;
  }
  table {
    border-collapse: collapse;
    width: 100%;
  }
  table.selectable {
    user-select: none;
  }
  tbody tr {
    background: var(--theme-bg-0);
  }
  tbody tr.selected {
    background: var(--theme-bg-3);
  }
  table:focus tbody tr.selected {
    background: var(--theme-bg-selected);
  }
  tbody tr.clickable:hover {
    background: var(--theme-bg-hover);
  }

  thead th {
    border: 1px solid var(--theme-border);
    background-color: var(--theme-bg-1);
    padding: 5px;
  }
  tbody td {
    border: 1px solid var(--theme-border);
  }
  tbody td {
    padding: 5px;
  }
  td.isHighlighted {
    background-color: var(--theme-bg-1);
  }

  td.clickable {
    cursor: pointer;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
    border-top: 1px solid var(--theme-border);
  }

  table th {
    border-left: none;
  }

  thead :global(tr:first-child) :global(th) {
    border-top: 1px solid var(--theme-border);
  }

  table td {
    border: 0px;
    border-bottom: 1px solid var(--theme-border);
    border-right: 1px solid var(--theme-border);
  }

  table {
    border-spacing: 0;
    border-collapse: separate;
    border-left: 1px solid var(--theme-border);
  }

  .empty-cell {
    background-color: var(--theme-bg-1);
  }

  .filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .filter-label {
    margin-right: 5px;
    color: var(--theme-text-2);
  }

  .details-wrap {
    padding: 10px;
    display: flex;
    flex-direction: column;
  }

  .details-wrap .row {
    display: flex;
  }

  .details-wrap .row div:first-child {
    width: 150px;
  }

  pre {
    overflow: auto;
    max-width: 50vw;
  }
</style>
